import { useMemo } from 'react'
import { sortBy } from 'lodash'
import { Season } from 'app/frontend/types/custom'
import {
  usePageInfo,
  useCharacterProgress,
} from 'app/frontend/base/config/queries'
import { ChartConfig } from '@/components/ui/chart'

export const useCharacterKeystoneGraph = (
  character?: wowaudit.Character,
  season?: Season,
  minKeyLevel?: number,
) => {
  const { data: progress } = useCharacterProgress(character?.id)
  const {
    data: { currentPeriod, currentExpansion },
  } = usePageInfo()

  const region = character?.realm.region

  return useMemo<{
    config: ChartConfig
    values: { label: string; total: number }[]
  }>(() => {
    const config: ChartConfig = {
      total: {
        label: 'Runs',
        color: character
          ? `var(--color-class-${character.characterClass
              .toLowerCase()
              .replace(' ', '-')})`
          : 'var(--color-primary)',
      },
    }

    if (!progress?.keystones || !season || !region) {
      return { config, values: [] }
    }

    const firstPeriod = season.first_period
    const seasons = sortBy(currentExpansion.seasons, 'first_period')
    const nextSeason = seasons.find((s) => s.first_period > firstPeriod)
    const untilPeriod = Math.min(
      nextSeason ? nextSeason.first_period - 1 : currentPeriod[region],
      currentPeriod[region],
    )
    const weekCount = untilPeriod - firstPeriod + 1

    const values = Array.from({ length: weekCount }, (_, index) => ({
      label: (index + 1).toString(),
      total: Object.values(
        progress.keystones[firstPeriod + index] ?? {},
      ).filter((run) => run.level >= (minKeyLevel ?? 0)).length,
    }))

    return { config, values }
  }, [
    progress,
    season,
    region,
    minKeyLevel,
    character,
    currentExpansion.seasons,
    currentPeriod,
  ])
}
